import React from 'react';
import ImportTrack from './ImportTrack';
import ShowTrack from './ShowTrack';
import { useRecordContext } from 'components/record/useRecordContext';
import { SubEventType } from 'types/App';
import { useFetch } from 'hooks/useFetchData';
import { LatLngLiteral } from 'leaflet';
import DeleteButton from 'components/forms/DeleteButton';
import { ResourceContextProvider } from 'components/record/ResourceContext';
import { RecordContextProvider } from 'components/record/RecordContext';
import EditButton from 'components/forms/EditButton';
import EditTrackForm from './EditTrackForm';
import ImportTrackFromGPS from './ImportTrackFromGPS';
import { Loader } from 'components/common/General';

const STAGE = {
  SHOW: 'show',
  IMPORT: 'import'
};

export type TrackPoint = LatLngLiteral & { time: number };

const canUploadCompetitorTrack = (subEvent: SubEventType, isVirtual: boolean) =>
  subEvent.event.status === 'finished' || isVirtual;

const CompetitorTrackForm = ({ subEvent }: CompetitorTrackFormProps) => {
  const { record: competitor } = useRecordContext();

  const subEventId = subEvent.id;
  const competitorId = competitor.id;
  const canUpload = canUploadCompetitorTrack(subEvent, !!competitor.isVirtual);

  const [stage, setStage] = React.useState<string>();
  const [track, setTrack] = React.useState<TrackPoint[]>([]);

  const { data, loading, refetch } = useFetch<{ item: { track: TrackPoint[] } }>('GET_ONE', 'CompetitorTrack', {
    variables: {
      subEventId,
      id: competitorId
    }
  });

  React.useEffect(() => setTrack((data?.item?.track as TrackPoint[] | undefined) || []), [data]);
  React.useEffect(() => {
    if (track.length > 0) {
      setStage(STAGE.SHOW);
    } else if (canUpload) {
      setStage(STAGE.IMPORT);
    }
  }, [track, canUpload]);

  if (loading) return <Loader />;

  const onSuccess = () => {
    setStage(STAGE.IMPORT);
  };

  if (!canUpload && track.length === 0) {
    return <React.Fragment>Track upload is only available for finished events.</React.Fragment>;
  }

  return (
    <ResourceContextProvider resource="CompetitorTrack" params={{ subEventId: subEvent.id, id: competitor.id }}>
      {stage === STAGE.IMPORT && canUpload && (
        <React.Fragment>
          <ImportTrack subEvent={subEvent} competitor={competitor} callback={refetch} />
          {!competitor.isVirtual && <ImportTrackFromGPS subEvent={subEvent} competitor={competitor} callback={refetch} />}
        </React.Fragment>
      )}
      {stage === STAGE.SHOW && (
        <React.Fragment>
          <ShowTrack subEvent={subEvent} track={track} color={competitor?.color} />
          {canUpload && (
            <RecordContextProvider refresh={refetch} value={{ track, id: competitorId }} id={competitorId}>
              <EditButton>
                <EditTrackForm subEvent={subEvent} color={competitor.color} data={track} />
              </EditButton>
              <DeleteButton onSuccess={onSuccess}>Are you sure you want to delete track?</DeleteButton>
            </RecordContextProvider>
          )}
        </React.Fragment>
      )}
    </ResourceContextProvider>
  );
};

export default CompetitorTrackForm;

type CompetitorTrackFormProps = {
  subEvent: SubEventType;
};
